import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { RecurringItem, Class, LongTermAssignment } from '@homework-app/shared';
import Header from './components/Header';
import PeriodsContainer from './components/PeriodsContainer';
import LongTermSection from './components/LongTermSection';
import SettingsScreen from './components/SettingsScreen';
import PhotoModal from './components/PhotoModal';
import TutorPage from './components/tutor/TutorPage';
import { ChecklistView } from './components/checklist/ChecklistView';
import { apiService } from './services/api';
import { offlineStorage } from './services/offlineStorage';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { getDateKey } from '@homework-app/shared';

type ViewType = 'checklist' | 'main' | 'settings' | 'tutor';

function App() {
  const { getToken } = useAuth();
  const [currentView, setCurrentView] = useState<ViewType>('checklist');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [homeworkData, setHomeworkData] = useState<{ [period: number]: any[] }>({});
  const [recurringHomework, setRecurringHomework] = useState<RecurringItem[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [assignments, setAssignments] = useState<LongTermAssignment[]>([]);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState(false);
  const { isOnline, wasOffline, resetWasOffline } = useNetworkStatus();

  useEffect(() => {
    initializeApp();
  }, []);

  useEffect(() => {
    loadHomeworkForDate(currentDate);
  }, [currentDate]);

  useEffect(() => {
    // Update API service online status
    apiService.setOnlineStatus(isOnline);

    // If we just came back online and were previously offline, sync
    if (isOnline && wasOffline) {
      handleSync();
      resetWasOffline();
    }
  }, [isOnline, wasOffline]);

  const initializeApp = async () => {
    try {
      // Set up Clerk token for API service
      apiService.setGetTokenFn(getToken);

      // Initialize offline storage first
      await offlineStorage.init();

      // Check if there are pending items to sync
      if (isOnline) {
        const queue = await offlineStorage.getSyncQueue();
        if (queue.length > 0) {
          // Sync BEFORE loading data
          await handleSync();
        }
      }

      // Now load initial data
      await loadInitialData();
    } catch (err) {
      console.error('Failed to initialize app:', err);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      await apiService.syncOfflineData();
      // Reload current date after sync
      await loadHomeworkForDate(currentDate);
    } catch (err) {
      console.error('Sync failed:', err);
      setError('Failed to sync offline data');
    } finally {
      setIsSyncing(false);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [recurring, loadedClasses, loadedAssignments] = await Promise.all([
        apiService.getRecurringHomework(),
        apiService.getClasses().catch(() => []),
        apiService.getAssignments().catch(() => [])
      ]);

      setRecurringHomework(recurring);
      setClasses(loadedClasses);
      setAssignments(loadedAssignments);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to connect to server. Using offline mode.');
      // Fall back to localStorage for offline functionality
      loadLocalData();
    } finally {
      setLoading(false);
    }
  };

  const loadLocalData = () => {
    const saved = localStorage.getItem('homeworkData');
    if (saved) {
      const localData = JSON.parse(saved);
      const dateKey = getDateKey(currentDate);
      setHomeworkData(localData[dateKey] || {});
    }

    const savedRecurring = localStorage.getItem('recurringHomework');
    if (savedRecurring) {
      setRecurringHomework(JSON.parse(savedRecurring));
    }

    const savedClasses = localStorage.getItem('classes');
    if (savedClasses) {
      setClasses(JSON.parse(savedClasses));
    }
  };

  const loadHomeworkForDate = async (date: Date) => {
    const dateKey = getDateKey(date);

    try {
      setLoading(true);
      const data = await apiService.getHomeworkForDate(dateKey);
      setHomeworkData(data);
    } catch (err) {
      console.error('Error loading homework:', err);
      // Fall back to localStorage
      const saved = localStorage.getItem('homeworkData');
      if (saved) {
        const localData = JSON.parse(saved);
        setHomeworkData(localData[dateKey] || {});
      }
    } finally {
      setLoading(false);
    }
  };

  const addHomework = async (period: number, subject: string = '') => {
    try {
      const dateKey = getDateKey(currentDate);
      // Text is pre-filled with subject name, subject is stored separately for querying
      const newItem = await apiService.createHomework(dateKey, period, subject, subject);

      setHomeworkData(prev => ({
        ...prev,
        [period]: [...(prev[period] || []), newItem]
      }));
    } catch (err) {
      console.error('Error adding homework:', err);
      setError('Failed to add homework');
    }
  };

  const updateHomework = async (id: string, text: string, completed: boolean) => {
    try {
      await apiService.updateHomework(id, text, completed);

      // Update local state
      setHomeworkData(prev => {
        const newData = { ...prev };
        for (const period in newData) {
          newData[period] = newData[period].map(item =>
            item.id === id ? { ...item, text, completed } : item
          );
        }

        // Save to offline storage
        const dateKey = getDateKey(currentDate);
        offlineStorage.saveHomeworkForDate(dateKey, newData).catch(err =>
          console.error('Failed to save to offline storage:', err)
        );

        return newData;
      });
    } catch (err) {
      console.error('Error updating homework:', err);
      setError('Failed to update homework');
    }
  };

  const deleteHomework = async (id: string) => {
    try {
      await apiService.deleteHomework(id);

      // Update local state
      setHomeworkData(prev => {
        const newData = { ...prev };
        for (const period in newData) {
          newData[period] = newData[period].filter(item => item.id !== id);
        }

        // Save to offline storage
        const dateKey = getDateKey(currentDate);
        offlineStorage.saveHomeworkForDate(dateKey, newData).catch(err =>
          console.error('Failed to save to offline storage:', err)
        );

        return newData;
      });
    } catch (err) {
      console.error('Error deleting homework:', err);
      setError('Failed to delete homework');
    }
  };

  const uploadPhoto = async (homeworkId: string, file: File) => {
    try {
      const photo = await apiService.uploadPhoto(homeworkId, file);

      // Update local state
      setHomeworkData(prev => {
        const newData = { ...prev };
        for (const period in newData) {
          newData[period] = newData[period].map(item =>
            item.id === homeworkId
              ? { ...item, photos: [...(item.photos || []), photo] }
              : item
          );
        }

        // Save to offline storage
        const dateKey = getDateKey(currentDate);
        offlineStorage.saveHomeworkForDate(dateKey, newData).catch(err =>
          console.error('Failed to save to offline storage:', err)
        );

        return newData;
      });
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo');
    }
  };

  const updateRecurringHomework = async (newRecurring: RecurringItem[]) => {
    try {
      const dateKey = getDateKey(currentDate);
      // Update recurring homework and also update existing items for the current date
      await apiService.updateRecurringHomework(newRecurring, true, dateKey);
      setRecurringHomework(newRecurring);

      // Reload current date's homework to reflect changes
      await loadHomeworkForDate(currentDate);
    } catch (err) {
      console.error('Error updating recurring homework:', err);
      setError('Failed to update recurring homework');
    }
  };

  const updateClasses = async (newClasses: Class[]) => {
    try {
      await apiService.updateClasses(newClasses);
      setClasses(newClasses);
    } catch (err) {
      console.error('Error updating classes:', err);
      setError('Failed to update classes');
    }
  };

  // Long-term assignment handlers
  const addAssignment = async (assignment: Omit<LongTermAssignment, 'id' | 'completed' | 'created_at' | 'updated_at'>) => {
    try {
      const newAssignment = await apiService.createAssignment(assignment);
      setAssignments(prev => [...prev, newAssignment]);
    } catch (err) {
      console.error('Error adding assignment:', err);
      setError('Failed to add assignment');
    }
  };

  const updateAssignment = async (id: string, updates: Partial<LongTermAssignment>) => {
    try {
      await apiService.updateAssignment(id, updates);
      setAssignments(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
    } catch (err) {
      console.error('Error updating assignment:', err);
      setError('Failed to update assignment');
    }
  };

  const deleteAssignment = async (id: string) => {
    try {
      await apiService.deleteAssignment(id);
      setAssignments(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Error deleting assignment:', err);
      setError('Failed to delete assignment');
    }
  };

  const changeDate = (days: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const openPhotoModal = (photoSrc: string) => {
    setSelectedPhoto(photoSrc);
    setPhotoModalOpen(true);
  };

  const clearError = () => {
    setError('');
  };

  if (currentView === 'checklist') {
    return (
      <div className="container">
        <div className="checklist-nav">
          <button onClick={() => setCurrentView('main')} className="btn-nav-secondary">
            Homework Tracker
          </button>
          <button onClick={() => setCurrentView('tutor')} className="btn-nav-secondary">
            Tutor
          </button>
          <button onClick={() => setCurrentView('settings')} className="btn-nav-secondary">
            Settings
          </button>
        </div>
        <ChecklistView apiService={apiService} />
      </div>
    );
  }

  if (currentView === 'settings') {
    return (
      <div className="container">
        <SettingsScreen
          recurringHomework={recurringHomework}
          classes={classes}
          onBack={() => setCurrentView('checklist')}
          onUpdateRecurring={updateRecurringHomework}
          onUpdateClasses={updateClasses}
        />
      </div>
    );
  }

  if (currentView === 'tutor') {
    return <TutorPage onClose={() => setCurrentView('checklist')} />;
  }

  return (
    <div className="container">
      {!isOnline && (
        <div className="banner banner--offline">
          🔌 Offline Mode - Changes will sync when you're back online
        </div>
      )}

      {isSyncing && (
        <div className="banner banner--syncing">
          🔄 Syncing data...
        </div>
      )}

      {error && (
        <div className="banner banner--error">
          <span>{error}</span>
          <button onClick={clearError} className="banner__dismiss">×</button>
        </div>
      )}

      {loading && (
        <div className="banner banner--loading">
          Loading...
        </div>
      )}

      <div style={{ marginBottom: '0.5rem' }}>
        <button
          onClick={() => setCurrentView('checklist')}
          style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 8, padding: '0.4rem 0.8rem', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}
        >
          ← Back to Checklist
        </button>
      </div>

      <Header
        currentDate={currentDate}
        onDateChange={changeDate}
        onToday={goToToday}
        onSettingsOpen={() => setCurrentView('settings')}
      />

      <div className="main-content">
        <div className="main-content__left">
          <PeriodsContainer
            homeworkData={homeworkData}
            classes={classes}
            onAddHomework={addHomework}
            onUpdateHomework={updateHomework}
            onDeleteHomework={deleteHomework}
            onPhotoUpload={uploadPhoto}
            onPhotoClick={openPhotoModal}
          />
        </div>
        <div className="main-content__right">
          <LongTermSection
            assignments={assignments}
            classes={classes}
            onAdd={addAssignment}
            onUpdate={updateAssignment}
            onDelete={deleteAssignment}
          />
        </div>
      </div>

      {photoModalOpen && (
        <PhotoModal
          photoSrc={selectedPhoto}
          onClose={() => setPhotoModalOpen(false)}
        />
      )}

      {/* Floating Tutor Button */}
      <button
        className="tutor-fab"
        onClick={() => setCurrentView('tutor')}
        aria-label="Open Homework Tutor"
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
        </svg>
        <span>Need Help?</span>
      </button>
    </div>
  );
}

export default App;